import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
    Box,
    Dialog,
    DialogContent,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BrushIcon from '@mui/icons-material/Brush';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckroomIcon from '@mui/icons-material/Checkroom';

import { SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';
import { SCUIBreadCrumb } from '../../../atomic/molecules';
import { SCUIContent } from '../../../atomic/organisms';
import {
    BatchOutputQuery,
    type BatchOutputItem,
    type BatchOutputRejectReason,
} from '../../../services/studio/StudioQuery/BatchOutput/BatchOutputQuery.services';
import { BatchOutputCommand } from '../../../services/studio/StudioCommand/BatchOutput/BatchOutputCommand.services';
import { GetS3PresignedDownloadUrls } from '../../../services/query/aiPrompt/S3BrowserQuery.services';
import { ComparePanel } from './ComparePanel';
import { useStudioBatchTrackingHub } from '../../../services/signalr/useStudioBatchTrackingHub';
import './BatchOutputControl.scss';

const PAGE_SIZE = 100;
const REALTIME_REFRESH_DEBOUNCE_MS = 1000;

const PHOTO_TYPE_ORDER: Record<string, number> = {
    FrontShot: 1,
    BackShot: 2,
    DetailShot: 3,
};

const sortQueueItems = (list: BatchOutputItem[]) =>
    [...list].sort((a, b) => {
        const barcodeCmp = (a.barcode || '').localeCompare(b.barcode || '', undefined, { numeric: true });
        if (barcodeCmp !== 0) {
            return barcodeCmp;
        }
        const typeA = PHOTO_TYPE_ORDER[a.photoTypeCode] ?? 99;
        const typeB = PHOTO_TYPE_ORDER[b.photoTypeCode] ?? 99;
        if (typeA !== typeB) {
            return typeA - typeB;
        }
        return a.rawImageId - b.rawImageId;
    });

type QueueTab = 'Pending' | 'Rejected';

const getCurrentUsername = (): string | null => {
    try {
        const username = JSON.parse(localStorage.getItem('checkOperator') || '{}')?.username;
        if (!username || typeof username !== 'string' || !username.trim()) {
            return null;
        }
        const trimmed = username.trim();
        if (trimmed.toLowerCase() === 'system') {
            return null;
        }
        return trimmed;
    } catch {
        return null;
    }
};

const requireUsername = async (t: (key: string) => string): Promise<string | null> => {
    const username = getCurrentUsername();
    if (username) {
        return username;
    }
    await Swal.fire({
        icon: 'warning',
        title: t('studio.batchOutputControl.userIdentityRequired'),
    });
    return null;
};

export const BatchOutputControl = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('en') ? 'en' : 'tr';

    const [queue, setQueue] = useState<QueueTab>('Pending');
    const [items, setItems] = useState<BatchOutputItem[]>([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [approvedCount, setApprovedCount] = useState(0);
    const [rejectedCount, setRejectedCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
    const imageUrlsRef = useRef<Record<string, string>>({});
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReasons, setRejectReasons] = useState<BatchOutputRejectReason[]>([]);
    const [rejectKey, setRejectKey] = useState('');
    const [rejectNote, setRejectNote] = useState('');
    const [extraPrompt, setExtraPrompt] = useState('');
    const [transitioning, setTransitioning] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const currentItem = items[currentIndex] ?? null;

    const loadPresignedUrls = useCallback(async (list: BatchOutputItem[]) => {
        const paths = list
            .flatMap((item) => [
                item.filePath,
                item.llmProcessedFilePath || item.processedFilePath,
            ])
            .filter(Boolean) as string[];
        if (paths.length === 0) {
            return;
        }

        const next: Record<string, string> = {};
        const keysToFetch: string[] = [];

        paths.forEach((path) => {
            if (imageUrlsRef.current[path] || next[path]) {
                return;
            }
            if (/^https?:\/\//i.test(path)) {
                next[path] = path;
                return;
            }
            keysToFetch.push(path);
        });

        if (keysToFetch.length === 0 && Object.keys(next).length === 0) {
            return;
        }

        try {
            if (keysToFetch.length > 0) {
                const presigned = await GetS3PresignedDownloadUrls(keysToFetch);
                presigned.items.forEach((entry) => {
                    if (entry.key && entry.url) {
                        next[entry.key] = entry.url;
                    }
                });
            }
            if (Object.keys(next).length === 0) {
                return;
            }
            imageUrlsRef.current = { ...imageUrlsRef.current, ...next };
            setImageUrls({ ...imageUrlsRef.current });
        } catch {
            // preview optional
        }
    }, []);

    const loadItems = useCallback(async (options?: { keepIndex?: boolean; silent?: boolean }) => {
        if (!options?.silent) {
            setLoading(true);
        }
        try {
            const username = getCurrentUsername();
            if (!username) {
                setItems([]);
                setPendingCount(0);
                setApprovedCount(0);
                setRejectedCount(0);
                setCurrentIndex(0);
                return;
            }

            const response = await BatchOutputQuery.GetItems({
                pageNumber: 1,
                pageSize: PAGE_SIZE,
                queue,
                assignee: username,
            });
            const sorted = sortQueueItems(response.items);
            setItems(sorted);
            setPendingCount(response.pendingCount);
            setApprovedCount(response.approvedCount);
            setRejectedCount(response.rejectedCount);
            if (!options?.keepIndex) {
                setCurrentIndex(0);
            } else {
                setCurrentIndex((idx) => Math.min(idx, Math.max(0, sorted.length - 1)));
            }
            await loadPresignedUrls(sorted);
        } catch {
            Swal.fire({
                icon: 'error',
                title: t('studio.batchOutputControl.loadError'),
            }).catch(() => undefined);
            if (!options?.silent) {
                setItems([]);
            }
        } finally {
            if (!options?.silent) {
                setLoading(false);
            }
        }
    }, [queue, loadPresignedUrls, t]);

    useEffect(() => {
        void loadItems();
    }, [loadItems]);

    useEffect(() => {
        void BatchOutputQuery.GetRejectReasons()
            .then(setRejectReasons)
            .catch(() => setRejectReasons([]));
    }, []);

    useEffect(
        () => () => {
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
            }
        },
        [],
    );

    const scheduleRefresh = useCallback(() => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
        }
        refreshTimerRef.current = setTimeout(() => {
            refreshTimerRef.current = null;
            void loadItems({ keepIndex: true, silent: true });
        }, REALTIME_REFRESH_DEBOUNCE_MS);
    }, [loadItems]);

    useStudioBatchTrackingHub({
        onRefresh: scheduleRefresh,
        onReconnected: () => void loadItems({ keepIndex: true, silent: true }),
    });

    // Kuyruk kısaldığında index taşmasın
    useEffect(() => {
        if (items.length === 0) {
            setCurrentIndex(0);
            return;
        }
        if (currentIndex > items.length - 1) {
            setCurrentIndex(items.length - 1);
        }
    }, [items.length, currentIndex]);

    // Sonraki 1-2 kare için presigned URL önceden al (otomatik geçişte boş kalmasın)
    useEffect(() => {
        if (items.length === 0) {
            return;
        }
        const ahead = items.slice(currentIndex, currentIndex + 3);
        void loadPresignedUrls(ahead);
    }, [items, currentIndex, loadPresignedUrls]);

    const positionLabel = useMemo(() => {
        if (items.length === 0) {
            return '0 / 0';
        }
        return `${currentIndex + 1} / ${items.length}`;
    }, [currentIndex, items.length]);

    const frameBadge = useMemo(() => {
        if (!currentItem) {
            return '';
        }
        const label =
            currentItem.photoTypeDisplayName ||
            currentItem.photoTypeCode ||
            t('studio.batchOutputControl.frameFallback');
        const sameBarcode = items.filter((item) => item.barcode === currentItem.barcode);
        const frameIndex = sameBarcode.findIndex((item) => item.rawImageId === currentItem.rawImageId);
        return `${label} (${Math.max(frameIndex, 0) + 1}/${sameBarcode.length || 1})`;
    }, [currentItem, items, t]);

    const goPrev = () => {
        if (currentIndex <= 0) {
            return;
        }
        setRegenerating(false);
        setTransitioning(true);
        setCurrentIndex((idx) => Math.max(0, idx - 1));
    };

    const goNext = () => {
        if (currentIndex >= items.length - 1) {
            return;
        }
        setRegenerating(false);
        setTransitioning(true);
        setCurrentIndex((idx) => Math.min(items.length - 1, idx + 1));
    };

    /** Onay/ret sonrası: kuyruktan çıkar, aynı indekste sonraki kareyi göster (otomatik geçiş). */
    const removeCurrentAndAdvance = useCallback(
        (removedRawImageId: number) => {
            setTransitioning(true);
            setItems((prev) => {
                const removedIndex = prev.findIndex((item) => item.rawImageId === removedRawImageId);
                if (removedIndex < 0) {
                    return prev;
                }

                const next = prev.filter((item) => item.rawImageId !== removedRawImageId);
                const nextIndex = next.length === 0 ? 0 : Math.min(removedIndex, next.length - 1);

                // setState batch: index aynı anda güncellenir
                setCurrentIndex(nextIndex);

                if (next.length === 0) {
                    setTransitioning(false);
                    queueMicrotask(() => {
                        void loadItems({ silent: true });
                    });
                }

                return next;
            });
        },
        [loadItems],
    );

    /** Photoshop aktarımı sonrası: barkodun tüm karelerini kuyruktan çıkar. */
    const removeBarcodeAndAdvance = useCallback(
        (barcode: string) => {
            setTransitioning(true);
            setItems((prev) => {
                const firstIndex = prev.findIndex((item) => item.barcode === barcode);
                const next = prev.filter((item) => item.barcode !== barcode);
                const nextIndex =
                    next.length === 0
                        ? 0
                        : Math.min(firstIndex < 0 ? 0 : firstIndex, next.length - 1);
                setCurrentIndex(nextIndex);

                if (next.length === 0) {
                    setTransitioning(false);
                    queueMicrotask(() => {
                        void loadItems({ silent: true });
                    });
                }

                return next;
            });
        },
        [loadItems],
    );

    const handleSendToPhotoshop = async () => {
        if (!currentItem?.barcode) {
            return;
        }

        const barcode = currentItem.barcode;
        const confirm = await Swal.fire({
            icon: 'question',
            title: t('studio.batchOutputControl.sendToPhotoshopConfirmTitle'),
            text: t('studio.batchOutputControl.sendToPhotoshopConfirmText', { barcode }),
            showCancelButton: true,
            confirmButtonText: t('studio.batchOutputControl.sendToPhotoshopConfirm'),
            cancelButtonText: t('studio.batchOutputControl.cancel'),
            confirmButtonColor: '#0284c7',
        });

        if (!confirm.isConfirmed) {
            return;
        }

        const username = await requireUsername(t);
        if (!username) {
            return;
        }

        setActionLoading(true);
        try {
            const result = await BatchOutputCommand.SendToPhotoshop(barcode, username);
            if (!BatchOutputCommand.isMutationSuccess(result.status)) {
                await Swal.fire({
                    icon: 'warning',
                    title: t('studio.batchOutputControl.sendToPhotoshopFailed'),
                    text: result.message,
                });
                return;
            }

            const removedCount = items.filter((item) => item.barcode === barcode).length;
            if (queue === 'Pending') {
                setPendingCount((c) => Math.max(0, c - removedCount));
            } else {
                setRejectedCount((c) => Math.max(0, c - removedCount));
            }

            removeBarcodeAndAdvance(barcode);

            void Swal.fire({
                icon: 'success',
                title: t('studio.batchOutputControl.sendToPhotoshopSuccess'),
                text: t('studio.batchOutputControl.sendToPhotoshopSuccessDetail', {
                    count: result.transferredFilesCount ?? 0,
                }),
                timer: 2000,
                showConfirmButton: false,
                toast: true,
                position: 'top-end',
            });
        } catch {
            await Swal.fire({
                icon: 'error',
                title: t('studio.batchOutputControl.sendToPhotoshopFailed'),
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleApprove = async () => {
        if (!currentItem) {
            return;
        }
        const username = await requireUsername(t);
        if (!username) {
            return;
        }

        const approvedId = currentItem.rawImageId;
        setActionLoading(true);
        try {
            const result = await BatchOutputCommand.Approve(approvedId, username);
            if (!BatchOutputCommand.isMutationSuccess(result.status)) {
                await Swal.fire({
                    icon: 'warning',
                    title: t('studio.batchOutputControl.approveFailed'),
                    text: result.message,
                });
                return;
            }

            setPendingCount((c) => Math.max(0, c - 1));
            setApprovedCount((c) => c + 1);

            // Önce kuyruktan düşür + sonraki kareye geç; toast engellemesin
            removeCurrentAndAdvance(approvedId);

            if (result.barcodeFullyApproved) {
                void Swal.fire({
                    icon: 'success',
                    title: t('studio.batchOutputControl.barcodeFullyApproved'),
                    text: t('studio.batchOutputControl.fileTransferDeferred'),
                    timer: 1800,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end',
                });
            }
        } catch {
            await Swal.fire({
                icon: 'error',
                title: t('studio.batchOutputControl.approveFailed'),
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectSubmit = async () => {
        if (!currentItem || !rejectKey) {
            return;
        }
        const username = await requireUsername(t);
        if (!username) {
            return;
        }

        const rejectedId = currentItem.rawImageId;
        setActionLoading(true);
        try {
            const result = await BatchOutputCommand.Reject(
                rejectedId,
                rejectKey,
                rejectNote,
                username,
            );
            if (!BatchOutputCommand.isMutationSuccess(result.status)) {
                await Swal.fire({
                    icon: 'warning',
                    title: t('studio.batchOutputControl.rejectFailed'),
                    text: result.message,
                });
                return;
            }

            setPendingCount((c) => Math.max(0, c - 1));
            setRejectedCount((c) => c + 1);
            setRejectOpen(false);
            setRejectKey('');
            setRejectNote('');
            removeCurrentAndAdvance(rejectedId);
        } catch {
            await Swal.fire({
                icon: 'error',
                title: t('studio.batchOutputControl.rejectFailed'),
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleRegenerate = async () => {
        if (!currentItem) {
            return;
        }

        const sourceUrl = imageUrls[currentItem.filePath];
        if (!sourceUrl) {
            await Swal.fire({
                icon: 'warning',
                title: t('studio.batchOutputControl.regenerateFailed'),
                text: t('studio.batchOutputControl.sourceImageMissing'),
            });
            return;
        }

        const username = await requireUsername(t);
        if (!username) {
            return;
        }

        setActionLoading(true);
        setRegenerating(true);
        try {
            const result = await BatchOutputCommand.Regenerate({
                rawImageId: currentItem.rawImageId,
                sourceImageUrl: sourceUrl,
                productBarcode: currentItem.barcode,
                username,
                extraPrompt: extraPrompt.trim() || undefined,
            });

            if (!BatchOutputCommand.isMutationSuccess(result.status)) {
                await Swal.fire({
                    icon: 'warning',
                    title: t('studio.batchOutputControl.regenerateFailed'),
                    text: result.message,
                });
                return;
            }

            const newPath = result.processedFilePath || '';
            setItems((prev) =>
                prev.map((item, idx) =>
                    idx === currentIndex
                        ? {
                              ...item,
                              llmProcessedFilePath: newPath || item.llmProcessedFilePath,
                              status: 'BatchProcessCompleted',
                              rejectKey: '',
                              rejectNote: '',
                          }
                        : item,
                ),
            );

            if (newPath) {
                await loadPresignedUrls([
                    {
                        ...currentItem,
                        llmProcessedFilePath: newPath,
                    },
                ]);
            }

            await Swal.fire({
                icon: 'success',
                title: t('studio.batchOutputControl.regenerateSuccess'),
                timer: 1600,
                showConfirmButton: false,
            });
        } catch {
            await Swal.fire({
                icon: 'error',
                title: t('studio.batchOutputControl.regenerateFailed'),
            });
        } finally {
            setActionLoading(false);
            setRegenerating(false);
        }
    };

    const rawUrl = currentItem ? imageUrls[currentItem.filePath] : undefined;
    const outputPath = currentItem
        ? currentItem.llmProcessedFilePath || currentItem.processedFilePath
        : '';
    const processedUrl = currentItem && outputPath ? imageUrls[outputPath] : undefined;
    const hasContent = Boolean(currentItem);

    // Presigned URL'ler hazır olunca geçiş skeleton'ını bırak (img onLoad ComparePanel'de).
    useEffect(() => {
        if (!transitioning || !currentItem) {
            return;
        }
        const rawReady = !currentItem.filePath || Boolean(rawUrl);
        const processedReady = !outputPath || Boolean(processedUrl);
        if (rawReady && processedReady) {
            setTransitioning(false);
        }
    }, [transitioning, currentItem, rawUrl, processedUrl, outputPath]);

    return (
        <SCUIContent>
            <SCUIBreadCrumb />

            <Box className="boc-page">
                {/* Header + metrics — mock Section */}
                <Box className="boc-header">
                    <Box className="boc-header__title-block" minWidth={0}>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                            <Box className="boc-module-badge">{t('studio.batchOutputControl.moduleBadge')}</Box>
                            <Typography className="boc-module-sub">{t('studio.batchOutputControl.moduleSub')}</Typography>
                        </Stack>
                        <Typography className="boc-page-title">{t('studio.batchOutputControl.title')}</Typography>
                        <Typography className="boc-page-subtitle">{t('studio.batchOutputControl.subtitle')}</Typography>
                    </Box>

                    <Stack direction="row" spacing={2} className="boc-metrics" flexWrap="wrap" useFlexGap>
                        <Box className="boc-metric boc-metric--pending">
                            <Box className="boc-metric__icon boc-metric__icon--pending">
                                <AccessTimeIcon fontSize="small" />
                            </Box>
                            <Box minWidth={0}>
                                <Typography className="boc-metric__label boc-metric__label--pending">
                                    {t('studio.batchOutputControl.metricPending')}
                                </Typography>
                                <Typography className="boc-metric__value">{pendingCount}</Typography>
                            </Box>
                        </Box>
                        <Box className="boc-metric boc-metric--approved">
                            <Box className="boc-metric__icon boc-metric__icon--approved">
                                <CheckCircleOutlineIcon fontSize="small" />
                            </Box>
                            <Box minWidth={0}>
                                <Typography className="boc-metric__label boc-metric__label--approved">
                                    {t('studio.batchOutputControl.metricApproved')}
                                </Typography>
                                <Typography className="boc-metric__value">{approvedCount}</Typography>
                            </Box>
                        </Box>
                    </Stack>
                </Box>

                {/* Queue tabs — rejected sekmesi (doküman); mock bozulmasın diye kompakt */}
                <Tabs
                    value={queue}
                    onChange={(_, value: QueueTab) => {
                        setQueue(value);
                        setExtraPrompt('');
                        setRegenerating(false);
                    }}
                    className="boc-tabs"
                    sx={{
                        mb: 2.5,
                        minHeight: 40,
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: 13,
                            minHeight: 40,
                        },
                    }}
                >
                    <Tab
                        value="Pending"
                        label={`${t('studio.batchOutputControl.tabPending')} (${pendingCount})`}
                    />
                    <Tab
                        value="Rejected"
                        label={`${t('studio.batchOutputControl.tabRejected')} (${rejectedCount})`}
                    />
                </Tabs>

                {loading ? <SCUILoadingLinear /> : null}

                {!loading && !hasContent ? (
                    <Box className="boc-empty">
                        <Typography color="text.secondary">{t('studio.batchOutputControl.noItems')}</Typography>
                    </Box>
                ) : null}

                {/* Sabit iskelet: içerik yokken de layout zıplamasın diye panel yükseklikleri korunur */}
                {hasContent ? (
                    <>
                        <Box className="boc-product-banner">
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'stretch', md: 'center' }}
                                spacing={2}
                                width="100%"
                            >
                                <Stack direction="row" spacing={2} alignItems="center" minWidth={0} flex={1}>
                                    <Box className="boc-product-banner__icon" flexShrink={0}>
                                        <CheckroomIcon />
                                    </Box>
                                    <Box minWidth={0} flex={1}>
                                        {transitioning ? (
                                            <Stack spacing={1}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Skeleton animation="wave" variant="rounded" width={220} height={22} />
                                                    <Skeleton animation="wave" variant="rounded" width={88} height={22} />
                                                </Stack>
                                                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                                    <Skeleton animation="wave" variant="text" width={120} />
                                                    <Skeleton animation="wave" variant="text" width={100} />
                                                    <Skeleton animation="wave" variant="text" width={90} />
                                                    <Skeleton animation="wave" variant="text" width={80} />
                                                </Stack>
                                            </Stack>
                                        ) : (
                                            <>
                                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                                    <Typography className="boc-product-banner__name" noWrap title={currentItem?.productName || currentItem?.barcode}>
                                                        {currentItem?.productName || currentItem?.barcode}
                                                    </Typography>
                                                    <Box className="boc-product-banner__frame">{frameBadge}</Box>
                                                </Stack>
                                                <Stack
                                                    direction="row"
                                                    spacing={2}
                                                    flexWrap="wrap"
                                                    useFlexGap
                                                    className="boc-product-banner__meta"
                                                >
                                                    <span>
                                                        {t('studio.batchOutputControl.barcodeLabel')}:{' '}
                                                        <strong>{currentItem?.barcode || '-'}</strong>
                                                    </span>
                                                    <span>
                                                        {t('studio.batchOutputControl.specialCodeLabel')}:{' '}
                                                        <strong>{currentItem?.specialCode || '-'}</strong>
                                                    </span>
                                                    <span>
                                                        {t('studio.batchOutputControl.colorCodeLabel')}:{' '}
                                                        <strong>{currentItem?.colorCode || '-'}</strong>
                                                    </span>
                                                </Stack>
                                            </>
                                        )}
                                    </Box>
                                </Stack>

                                <Box className="boc-queue-nav" flexShrink={0}>
                                    <Tooltip title={t('studio.batchOutputControl.prevProduct')}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                className="boc-queue-nav__btn"
                                                onClick={goPrev}
                                                disabled={currentIndex <= 0 || actionLoading || transitioning}
                                                aria-label={t('studio.batchOutputControl.prevProduct')}
                                            >
                                                <NavigateBeforeIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Typography className="boc-queue-nav__pos" component="span">
                                        {positionLabel}
                                    </Typography>
                                    <Tooltip title={t('studio.batchOutputControl.nextProduct')}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                className="boc-queue-nav__btn"
                                                onClick={goNext}
                                                disabled={currentIndex >= items.length - 1 || actionLoading || transitioning}
                                                aria-label={t('studio.batchOutputControl.nextProduct')}
                                            >
                                                <NavigateNextIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </Stack>
                        </Box>

                        <Box className="boc-compare-grid">
                            <ComparePanel
                                variant="raw"
                                title={t('studio.batchOutputControl.rawPanelTitle')}
                                badge={t('studio.batchOutputControl.rawBadge')}
                                imageUrl={rawUrl}
                                fileLabel={currentItem?.fileName}
                                emptyLabel={t('studio.batchOutputControl.imageLoading')}
                                forceLoading={transitioning || Boolean(currentItem?.filePath && !rawUrl)}
                            />
                            <ComparePanel
                                variant="batch"
                                title={t('studio.batchOutputControl.processedPanelTitle')}
                                badge={t('studio.batchOutputControl.processedBadge')}
                                imageUrl={regenerating ? undefined : processedUrl}
                                fileLabel={outputPath?.split('/').pop()}
                                emptyLabel={t('studio.batchOutputControl.imageLoading')}
                                forceLoading={
                                    regenerating
                                    || transitioning
                                    || Boolean(outputPath && !processedUrl)
                                }
                            />
                        </Box>

                        <Box
                            className={`boc-action-bar${queue === 'Rejected' ? ' boc-action-bar--stack' : ''}`}
                        >
                            <Stack spacing={1.5} minWidth={0} flex={1}>
                                <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
                                    <InfoOutlinedIcon className="boc-action-bar__info-icon" fontSize="small" />
                                    <Typography className="boc-action-bar__hint">
                                        {t('studio.batchOutputControl.actionHint')}
                                    </Typography>
                                </Stack>
                                {queue === 'Rejected' ? (
                                    <>
                                        <TextField
                                            className="boc-extra-prompt"
                                            multiline
                                            minRows={3}
                                            maxRows={16}
                                            fullWidth
                                            label={t('studio.batchOutputControl.extraPromptLabel')}
                                            placeholder={t('studio.batchOutputControl.extraPromptPlaceholder')}
                                            value={extraPrompt}
                                            onChange={(e) => setExtraPrompt(e.target.value)}
                                            disabled={actionLoading}
                                            slotProps={{
                                                input: {
                                                    className: 'boc-extra-prompt__input',
                                                },
                                            }}
                                        />
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            flexWrap="wrap"
                                            useFlexGap
                                            className="boc-action-bar__actions"
                                        >
                                            <SCUIButton
                                                type="ghost"
                                                sizeType="default"
                                                size="s"
                                                text={t('studio.batchOutputControl.sendToPhotoshop')}
                                                buttonType="button"
                                                showStartIcon
                                                startIcon={<BrushIcon fontSize="small" />}
                                                onClick={() => void handleSendToPhotoshop()}
                                                disabled={actionLoading}
                                                className="boc-decision-btn boc-decision-btn--photoshop"
                                            />
                                            <SCUIButton
                                                type="ghost"
                                                sizeType="default"
                                                size="s"
                                                text={t('studio.batchOutputControl.regenerate')}
                                                buttonType="button"
                                                showStartIcon
                                                startIcon={<AutoAwesomeIcon fontSize="small" />}
                                                onClick={() => void handleRegenerate()}
                                                disabled={actionLoading}
                                                className="boc-decision-btn boc-decision-btn--ghost"
                                            />
                                        </Stack>
                                    </>
                                ) : null}
                            </Stack>

                            {queue === 'Pending' ? (
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    flexShrink={0}
                                    flexWrap="wrap"
                                    useFlexGap
                                    alignItems="flex-end"
                                >
                                    <SCUIButton
                                        type="secondary"
                                        sizeType="default"
                                        size="s"
                                        text={t('studio.batchOutputControl.reject')}
                                        buttonType="button"
                                        showStartIcon
                                        startIcon={<CloseIcon fontSize="small" />}
                                        onClick={() => setRejectOpen(true)}
                                        disabled={actionLoading}
                                        className="boc-decision-btn boc-decision-btn--reject"
                                    />
                                    <SCUIButton
                                        type="primary"
                                        sizeType="default"
                                        size="s"
                                        text={t('studio.batchOutputControl.approveNext')}
                                        buttonType="button"
                                        showStartIcon
                                        startIcon={<CheckIcon fontSize="small" />}
                                        onClick={() => void handleApprove()}
                                        disabled={actionLoading}
                                        className="boc-decision-btn boc-decision-btn--approve"
                                    />
                                </Stack>
                            ) : null}
                        </Box>
                    </>
                ) : null}
            </Box>

            <Dialog
                open={rejectOpen}
                onClose={() => !actionLoading && setRejectOpen(false)}
                fullWidth
                maxWidth="xs"
                slotProps={{ paper: { className: 'boc-reject-dialog' } }}
            >
                <Box className="boc-reject-dialog__header">
                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        className="boc-reject-dialog__header-main"
                    >
                        <Box className="boc-reject-dialog__icon">
                            <WarningAmberIcon fontSize="small" />
                        </Box>
                        <Typography className="boc-reject-dialog__title">
                            {t('studio.batchOutputControl.rejectModalTitle')}
                        </Typography>
                    </Stack>
                    <IconButton
                        className="boc-reject-dialog__close"
                        size="small"
                        onClick={() => setRejectOpen(false)}
                        disabled={actionLoading}
                        aria-label={t('studio.batchOutputControl.cancel')}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <DialogContent className="boc-reject-dialog__body" dividers={false}>
                    <Typography className="boc-reject-dialog__hint">
                        {t('studio.batchOutputControl.rejectModalHint')}
                    </Typography>
                    <Box className="boc-reject-dialog__field">
                        <Typography component="label" className="boc-reject-dialog__label">
                            {t('studio.batchOutputControl.rejectReason')} <span>*</span>
                        </Typography>
                        <FormControl fullWidth size="small" className="boc-reject-dialog__control">
                            <Select
                                displayEmpty
                                value={rejectKey}
                                onChange={(e) => setRejectKey(String(e.target.value))}
                            >
                                <MenuItem value="">
                                    <em>{t('studio.batchOutputControl.rejectReasonPlaceholder')}</em>
                                </MenuItem>
                                {rejectReasons.map((reason) => (
                                    <MenuItem key={reason.key} value={reason.key}>
                                        {lang === 'en' ? reason.labelEn : reason.labelTr}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box className="boc-reject-dialog__field boc-reject-dialog__field--last">
                        <Typography component="label" className="boc-reject-dialog__label">
                            {t('studio.batchOutputControl.rejectNote')}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={8}
                            size="small"
                            className="boc-reject-dialog__control boc-reject-dialog__note"
                            placeholder={t('studio.batchOutputControl.rejectNotePlaceholder')}
                            value={rejectNote}
                            onChange={(e) => setRejectNote(e.target.value)}
                            slotProps={{
                                input: {
                                    className: 'boc-reject-dialog__note-input',
                                },
                            }}
                        />
                    </Box>
                </DialogContent>
                <Box className="boc-reject-dialog__footer">
                    <SCUIButton
                        type="secondary"
                        sizeType="default"
                        size="m"
                        text={t('studio.batchOutputControl.cancel')}
                        buttonType="button"
                        onClick={() => setRejectOpen(false)}
                        disabled={actionLoading}
                    />
                    <SCUIButton
                        type="error"
                        sizeType="default"
                        size="m"
                        text={t('studio.batchOutputControl.confirmReject')}
                        buttonType="button"
                        disabled={!rejectKey || actionLoading}
                        onClick={() => void handleRejectSubmit()}
                    />
                </Box>
            </Dialog>
        </SCUIContent>
    );
};
